import { writable, derived, get } from 'svelte/store';
import { browser } from '$app/environment';
import { authStore } from './auth';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'system';

export interface Theme {
	id: string;
	name: string;
	displayName: string;
	mode: ThemeMode;
	category: 'built-in' | 'custom';
}

// Built-in themes inspired by VS Code and Linear
export const BUILT_IN_THEMES: Theme[] = [
	{
		id: 'classic-light',
		name: 'classic-light',
		displayName: 'Classic Light',
		mode: 'light',
		category: 'built-in'
	},
	{
		id: 'classic-dark',
		name: 'classic-dark',
		displayName: 'Classic Dark',
		mode: 'dark',
		category: 'built-in'
	},
	{
		id: 'linear-light',
		name: 'linear-light',
		displayName: 'Linear Light',
		mode: 'light',
		category: 'built-in'
	},
	{
		id: 'linear-dark',
		name: 'linear-dark',
		displayName: 'Linear Dark',
		mode: 'dark',
		category: 'built-in'
	},
	{
		id: 'minimal-light',
		name: 'minimal-light',
		displayName: 'Minimal Light',
		mode: 'light',
		category: 'built-in'
	},
	{
		id: 'minimal-dark',
		name: 'minimal-dark',
		displayName: 'Minimal Dark',
		mode: 'dark',
		category: 'built-in'
	}
];

// System theme detection
function getSystemTheme(): ThemeMode {
	if (!browser) return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Stores
export const themePreference = writable<ThemePreference>('system');
export const currentTheme = writable<Theme>(BUILT_IN_THEMES[0]); // Default to classic-light
export const systemTheme = writable<ThemeMode>(getSystemTheme());

// Derived store for the effective theme mode
export const effectiveThemeMode = derived(
	[themePreference, currentTheme, systemTheme],
	([preference, theme, system]) => {
		if (preference === 'system') {
			return system;
		}
		return theme.mode;
	}
);

// Theme utilities
export const themeUtils = {
	// Get all themes for a specific mode
	getThemesByMode: (mode: ThemeMode): Theme[] => {
		return BUILT_IN_THEMES.filter((theme) => theme.mode === mode);
	},

	// Get theme by ID
	getThemeById: (id: string): Theme | undefined => {
		return BUILT_IN_THEMES.find((theme) => theme.id === id);
	},

	// Get light/dark pair for current theme
	getThemePair: (currentThemeId: string): { light: Theme; dark: Theme } | null => {
		const current = BUILT_IN_THEMES.find((t) => t.id === currentThemeId);
		if (!current) return null;

		const baseName = current.name.replace(/-light|-dark$/, '');
		const light = BUILT_IN_THEMES.find((t) => t.name === `${baseName}-light`);
		const dark = BUILT_IN_THEMES.find((t) => t.name === `${baseName}-dark`);

		if (light && dark) {
			return { light, dark };
		}
		return null;
	},

	// Set theme preference (light/dark/system)
	setThemePreference: async (preference: ThemePreference) => {
		themePreference.set(preference);

		// Auto-select appropriate theme based on preference
		const current = get(currentTheme);
		const pair = themeUtils.getThemePair(current.id);

		if (preference === 'light' && pair?.light) {
			await themeUtils.setTheme(pair.light);
		} else if (preference === 'dark' && pair?.dark) {
			await themeUtils.setTheme(pair.dark);
		} else if (preference === 'system') {
			// For system preference, choose based on current system theme
			const systemMode = get(systemTheme);
			if (systemMode === 'dark' && pair?.dark) {
				await themeUtils.setTheme(pair.dark);
			} else if (systemMode === 'light' && pair?.light) {
				await themeUtils.setTheme(pair.light);
			}
		}

		// Persist to localStorage
		if (browser) {
			localStorage.setItem('theme-preference', preference);
		}

		// Sync with backend if user is logged in
		const authState = get(authStore);
		if (authState.user) {
			try {
				// Import the API function dynamically to avoid circular dependencies
				const { updateCurrentUserApiUsersMePatch } =
					await import('$lib/api/generated/users/users.js');
				// Response is now the direct data from the API
				await updateCurrentUserApiUsersMePatch({
					theme_preference: preference
				});
			} catch (error) {
				console.warn('Failed to sync theme preference to backend:', error);
			}
		}
	},

	// Set specific theme
	setTheme: async (theme: Theme) => {
		currentTheme.set(theme);

		// Persist to localStorage
		if (browser) {
			localStorage.setItem('current-theme', theme.id);
		}

		// Auto-adjust preference based on theme mode
		const preference = get(themePreference);
		if (preference === 'system') {
			// If system preference, switch to explicit mode
			await themeUtils.setThemePreference(theme.mode);
		}
	},

	// Toggle between light and dark theme of the same family
	toggleTheme: async () => {
		const current = get(currentTheme);
		const pair = themeUtils.getThemePair(current.id);

		if (pair) {
			const newTheme = current.mode === 'light' ? pair.dark : pair.light;
			await themeUtils.setTheme(newTheme);
		} else {
			// Fallback: switch to opposite mode with classic theme
			const newMode = current.mode === 'light' ? 'dark' : 'light';
			const fallbackTheme = BUILT_IN_THEMES.find((t) => t.name === `classic-${newMode}`);
			if (fallbackTheme) {
				await themeUtils.setTheme(fallbackTheme);
			}
		}
	},

	// Initialize theme system
	initialize: () => {
		if (!browser) return;

		// Load from localStorage
		const savedPreference = localStorage.getItem('theme-preference') as ThemePreference;
		const savedTheme = localStorage.getItem('current-theme');

		if (savedPreference && ['light', 'dark', 'system'].includes(savedPreference)) {
			themePreference.set(savedPreference);
		}

		if (savedTheme) {
			const theme = themeUtils.getThemeById(savedTheme);
			if (theme) {
				currentTheme.set(theme);
			}
		}

		// Load from user profile if logged in
		const authState = get(authStore);
		if (authState.user?.theme_preference) {
			themePreference.set(authState.user.theme_preference as ThemePreference);
		}

		// Listen for system theme changes
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleSystemThemeChange = (e: MediaQueryListEvent) => {
			systemTheme.set(e.matches ? 'dark' : 'light');
		};

		mediaQuery.addEventListener('change', handleSystemThemeChange);

		// Cleanup function
		return () => {
			mediaQuery.removeEventListener('change', handleSystemThemeChange);
		};
	}
};

// Apply theme to DOM
export function applyTheme(mode: ThemeMode, theme: Theme) {
	if (!browser) return;

	const html = document.documentElement;

	// Remove all theme classes
	html.classList.remove('dark', 'light');
	BUILT_IN_THEMES.forEach((t) => {
		html.classList.remove(`theme-${t.id}`);
	});

	// Add new theme classes
	html.classList.add(mode);
	html.classList.add(`theme-${theme.id}`);

	// Set data attributes for CSS targeting
	html.setAttribute('data-theme', theme.id);
	html.setAttribute('data-theme-mode', mode);
}

// Reactive theme application
if (browser) {
	// Apply theme when effective mode or current theme changes
	effectiveThemeMode.subscribe((mode) => {
		const theme = get(currentTheme);
		applyTheme(mode, theme);
	});

	currentTheme.subscribe((theme) => {
		const mode = get(effectiveThemeMode);
		applyTheme(mode, theme);
	});
}
